import { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, ClipboardList, Pencil, Plus, Save, Search, Trash2 } from 'lucide-react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AxiosError } from 'axios';
import { Button } from '@/Components/ui/Button';
import { Card } from '@/Components/ui/Card';
import { Checkbox } from '@/Components/ui/Checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/Components/ui/Dialog';
import { IconButton } from '@/Components/ui/IconButton';
import { Input } from '@/Components/ui/Input';
import { Select } from '@/Components/ui/Select';
import { PageHeader } from '@/Components/data/PageHeader';
import DecimalInput from '@/Components/forms/DecimalInput';
import { Field } from '@/Components/forms/Field';
import FormStickyFooter from '@/Components/forms/FormStickyFooter';
import fichaTecnicaService, { type FichaTecnicaInput, type FichaTecnicaItem } from '@/services/fichaTecnica';
import produtoService, { type Produto } from '@/services/produto';
import { getUuidCandidate, isUuid } from '@/utils/uuid';

const TIPOS_PRODUTO_ACABADO = ['PA', '04'];

const FORM_VAZIO: FichaTecnicaInput = {
  produto_id: '',
  versao: '1.0',
  rendimento: 1,
  perda_padrao_percentual: 0,
  status: 'RASCUNHO',
  itens: [],
};

type ApiValidationResponse = {
  message?: string;
  errors?: Record<string, string[]>;
};

const produtoLabel = (produto: Produto) => `${produto.codigo || '-'} - ${produto.descricao || ''}`;

const fieldLabels: Record<string, string> = {
  produto_id: 'Produto acabado',
  versao: 'Versão',
  rendimento: 'Rendimento',
  perda_padrao_percentual: 'Perda padrão',
  status: 'Status',
};

function validationLabel(field: string) {
  const itemMatch = field.match(/^itens\.(\d+)\.(.+)$/);
  if (itemMatch) {
    const row = Number(itemMatch[1]) + 1;
    const labels: Record<string, string> = {
      produto_id: 'produto',
      quantidade: 'quantidade',
      unidade: 'unidade',
      perda_percentual: 'perda',
      sequencia: 'sequência',
      substituto_id: 'substituto',
    };
    return `Componente ${row} - ${labels[itemMatch[2]] || itemMatch[2]}`;
  }

  return fieldLabels[field] || field;
}

function extractErrorMessages(error: unknown): string[] {
  if (error instanceof AxiosError) {
    const data = error.response?.data as ApiValidationResponse | undefined;
    if (data?.errors) {
      return Object.entries(data.errors).flatMap(([field, messages]) =>
        messages.map((message) => `${validationLabel(field)}: ${message}`),
      );
    }
    if (data?.message) return [data.message];

    if (data && typeof data === 'object') {
      const topLevelErrors = Object.entries(data).flatMap(([field, value]) => {
        if (!Array.isArray(value)) return [];
        return value.map((message) => `${validationLabel(field)}: ${message}`);
      });
      if (topLevelErrors.length > 0) return topLevelErrors;
    }
  }

  return ['Não foi possível salvar a ficha técnica. Verifique os dados e tente novamente.'];
}

type ProductAutocompleteFieldProps = {
  id?: string;
  produtos: Produto[];
  value?: string | null;
  onChange: (value: string) => void;
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
  emptyLabel?: string;
};

function ProductAutocompleteField({
  id,
  produtos,
  value,
  onChange,
  disabled = false,
  required = false,
  placeholder = 'Buscar produto...',
  emptyLabel = 'Nenhum produto encontrado',
}: ProductAutocompleteFieldProps) {
  const [open, setOpen] = useState(false);
  const selectedProduto = useMemo(
    () => produtos.find((produto) => getUuidCandidate(produto) === value),
    [produtos, value],
  );
  const [text, setText] = useState('');
  const termo = text.trim().toLowerCase();

  useEffect(() => {
    setText(selectedProduto ? produtoLabel(selectedProduto) : '');
  }, [selectedProduto]);

  const sugestoes = useMemo(() => {
    if (!termo) return produtos.slice(0, 12);
    return produtos
      .filter((produto) => produtoLabel(produto).toLowerCase().includes(termo))
      .slice(0, 12);
  }, [produtos, termo]);

  return (
    <div className="nx-autocomplete-filter">
      <div className="nx-search-field nx-search-field--filter nx-autocomplete-filter__control">
        <Search size={15} strokeWidth={2} aria-hidden />
        <input
          id={id}
          type="search"
          value={text}
          disabled={disabled}
          required={required}
          placeholder={placeholder}
          aria-label={placeholder}
          onChange={(event) => {
            setText(event.target.value);
            if (value) onChange('');
            setOpen(true);
          }}
          onFocus={() => !disabled && setOpen(true)}
          onBlur={() => window.setTimeout(() => setOpen(false), 140)}
        />
        {text && !disabled ? (
          <button
            type="button"
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => {
              setText('');
              onChange('');
            }}
            aria-label="Limpar produto"
          >
            ×
          </button>
        ) : null}
      </div>
      {open && !disabled ? (
        <div className="nx-autocomplete-filter__menu">
          {sugestoes.length === 0 ? (
            <div className="nx-autocomplete-filter__empty">{emptyLabel}</div>
          ) : (
            sugestoes.map((produto) => {
              const uuid = getUuidCandidate(produto) || '';
              return (
                <button
                  type="button"
                  key={produto.id}
                  className={uuid === value ? 'is-selected' : undefined}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => {
                    onChange(uuid);
                    setText(produtoLabel(produto));
                    setOpen(false);
                  }}
                >
                  <strong>{produto.codigo || '-'}</strong>
                  <span>{produto.descricao}</span>
                </button>
              );
            })
          )}
        </div>
      ) : null}
    </div>
  );
}

export default function FichaTecnicaForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const editando = Boolean(id);
  const visualizando = location.pathname.endsWith('/visualizar');
  const [form, setForm] = useState<FichaTecnicaInput>(FORM_VAZIO);
  const [produtos, setProdutos] = useState<Produto[]>([]);
  const [loading, setLoading] = useState(editando);
  const [salvando, setSalvando] = useState(false);
  const [modalComponentesAberto, setModalComponentesAberto] = useState(false);
  const [buscaComponente, setBuscaComponente] = useState('');
  const [componentesSelecionados, setComponentesSelecionados] = useState<string[]>([]);
  const [substitutoItemIndex, setSubstitutoItemIndex] = useState<number | null>(null);
  const [buscaSubstituto, setBuscaSubstituto] = useState('');
  const [erros, setErros] = useState<string[]>([]);

  const produtosValidos = useMemo(() => produtos.filter((produto) => getUuidCandidate(produto)), [produtos]);
  const produtosAcabados = useMemo(
    () => produtosValidos.filter((produto) => TIPOS_PRODUTO_ACABADO.includes(produto.tipo_produto_codigo || '')),
    [produtosValidos],
  );
  const produtosFicha = produtosAcabados.length > 0 ? produtosAcabados : produtosValidos;
  const produtosComponentes = useMemo(() => {
    const termo = buscaComponente.trim().toLowerCase();
    const produtosJaAdicionados = new Set((form.itens ?? []).map((item) => item.produto_id).filter(Boolean));

    return produtosValidos
      .filter((produto) => !produtosJaAdicionados.has(getUuidCandidate(produto) || ''))
      .filter((produto) => {
        if (!termo) return true;
        return produtoLabel(produto).toLowerCase().includes(termo);
      });
  }, [buscaComponente, form.itens, produtosValidos]);
  const produtosSubstitutos = useMemo(() => {
    const termo = buscaSubstituto.trim().toLowerCase();
    const produtoAtualId = substitutoItemIndex === null ? '' : form.itens?.[substitutoItemIndex]?.produto_id;

    return produtosValidos
      .filter((produto) => getUuidCandidate(produto) !== produtoAtualId)
      .filter((produto) => {
        if (!termo) return true;
        return produtoLabel(produto).toLowerCase().includes(termo);
      });
  }, [buscaSubstituto, form.itens, produtosValidos, substitutoItemIndex]);

  useEffect(() => {
    produtoService.listar().then(setProdutos).catch(console.error);
  }, []);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    fichaTecnicaService.buscarPorId(id)
      .then((data) => setForm({
        produto_id: data.produto_id,
        versao: data.versao,
        rendimento: data.rendimento,
        perda_padrao_percentual: data.perda_padrao_percentual ?? 0,
        status: data.status,
        itens: data.itens ?? [],
      }))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [id]);

  const atualizarItem = (index: number, patch: Partial<FichaTecnicaItem>) => {
    const itens = [...(form.itens ?? [])];
    itens[index] = { ...itens[index], ...patch };

    if (patch.produto_id) {
      const produto = produtos.find((item) => getUuidCandidate(item) === patch.produto_id);
      itens[index].unidade = produto?.unidade || '';
    }

    setForm({ ...form, itens });
  };

  const adicionarItem = () => {
    setBuscaComponente('');
    setComponentesSelecionados([]);
    setModalComponentesAberto(true);
  };

  const confirmarComponentes = () => {
    if (componentesSelecionados.length === 0) return;
    const sequenciaInicial = form.itens?.length ?? 0;
    const novosItens: FichaTecnicaItem[] = componentesSelecionados.map((produtoId, index) => {
      const produto = produtos.find((item) => getUuidCandidate(item) === produtoId);
      return {
        produto_id: produtoId,
        quantidade: 0,
        unidade: produto?.unidade || '',
        perda_percentual: 0,
        sequencia: sequenciaInicial + index + 1,
        substituto_id: null,
      };
    });

    setForm({
      ...form,
      itens: [...(form.itens ?? []), ...novosItens],
    });
    setModalComponentesAberto(false);
    setComponentesSelecionados([]);
    setBuscaComponente('');
  };

  const toggleComponenteSelecionado = (produtoId: string) => {
    setComponentesSelecionados((selecionados) =>
      selecionados.includes(produtoId)
        ? selecionados.filter((item) => item !== produtoId)
        : [...selecionados, produtoId],
    );
  };

  const produtoPorId = (produtoId?: string | null) => produtos.find((produto) => getUuidCandidate(produto) === produtoId);

  const abrirModalSubstituto = (index: number) => {
    setSubstitutoItemIndex(index);
    setBuscaSubstituto('');
  };

  const selecionarSubstituto = (produtoId: string) => {
    if (substitutoItemIndex === null) return;
    atualizarItem(substitutoItemIndex, { substituto_id: produtoId });
    setSubstitutoItemIndex(null);
    setBuscaSubstituto('');
  };

  const limparSubstituto = (index: number) => {
    atualizarItem(index, { substituto_id: null });
  };

  const removerItem = (index: number) => {
    setForm({ ...form, itens: (form.itens ?? []).filter((_, itemIndex) => itemIndex !== index) });
  };

  const custoCalculado = useMemo(() => {
    return (form.itens ?? []).reduce((total, item) => {
      const produto = produtos.find((produtoItem) => getUuidCandidate(produtoItem) === item.produto_id);
      return total + (Number(produto?.custo_padrao) || 0) * (Number(item.quantidade) || 0);
    }, 0);
  }, [form.itens, produtos]);

  const salvar = async () => {
    if (visualizando) return;
    const errosFormulario: string[] = [];

    if (!isUuid(form.produto_id)) errosFormulario.push('Produto acabado: selecione um produto válido na lista.');
    if (!form.versao) errosFormulario.push('Versão: informe a versão da ficha técnica.');
    if (!form.rendimento) errosFormulario.push('Rendimento: informe um valor maior que zero.');
    (form.itens ?? []).forEach((item, index) => {
      if (!isUuid(item.produto_id)) errosFormulario.push(`Componente ${index + 1} - produto: selecione um produto válido.`);
      if (!item.quantidade || Number(item.quantidade) <= 0) errosFormulario.push(`Componente ${index + 1} - quantidade: informe um valor maior que zero.`);
      if (item.substituto_id && !isUuid(item.substituto_id)) errosFormulario.push(`Componente ${index + 1} - substituto: selecione um produto substituto válido.`);
    });

    if (errosFormulario.length > 0) {
      setErros(errosFormulario);
      return;
    }

    setErros([]);
    setSalvando(true);
    try {
      const payload = {
        produto_id: form.produto_id,
        versao: form.versao,
        rendimento: Number(form.rendimento),
        perda_padrao_percentual: Number(form.perda_padrao_percentual) || 0,
        status: form.status,
        itens: (form.itens ?? []).map((item) => ({
          produto_id: item.produto_id,
          quantidade: Number(item.quantidade),
          unidade: item.unidade,
          perda_percentual: Number(item.perda_percentual) || 0,
          sequencia: item.sequencia ?? 0,
          substituto_id: item.substituto_id || null,
        })),
      };

      if (id) await fichaTecnicaService.atualizar(id, payload);
      else await fichaTecnicaService.criar(payload);
      navigate('/producao/fichas-tecnicas');
    } catch (error) {
      console.error('Erro ao salvar ficha técnica', error);
      setErros(extractErrorMessages(error));
    } finally {
      setSalvando(false);
    }
  };

  const title = visualizando ? 'Visualizar Ficha Técnica' : editando ? 'Editar Ficha Técnica' : 'Nova Ficha Técnica';
  const description = visualizando
    ? 'Consulte os dados gerais e os componentes desta ficha técnica.'
    : editando
      ? 'Atualize os dados gerais e os componentes da ficha técnica.'
      : 'Preencha os dados gerais e os componentes da nova ficha técnica.';

  return (
    <form className="nx-page nx-sngpc-form-page" noValidate onSubmit={(event) => { event.preventDefault(); void salvar(); }}>
      <PageHeader
        icon={<ClipboardList size={21} />}
        title={title}
        description={description}
        actions={<Button type="button" variant="ghost" onClick={() => navigate('/producao/fichas-tecnicas')}><ArrowLeft size={16} /> Voltar</Button>}
      />

      <Card className="nx-page-card nx-sngpc-form-card">
        {erros.length > 0 ? (
          <div className="nx-form-alert nx-form-alert--error" role="alert">
            <strong>Não foi possível salvar a ficha técnica.</strong>
            <ul>
              {erros.map((erro, index) => (
                <li key={`${erro}-${index}`}>{erro}</li>
              ))}
            </ul>
          </div>
        ) : null}

        <fieldset disabled={loading || visualizando} style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}>
          <section className="nx-sngpc-form-section">
            <div className="nx-sngpc-form-section__header"><h2>Dados da ficha</h2></div>
            <div className="nx-sngpc-page-form-grid">
              <Field label="Produto acabado *" htmlFor="ficha-produto" className="nx-sngpc-field--full">
                <ProductAutocompleteField
                  id="ficha-produto"
                  produtos={produtosFicha}
                  value={form.produto_id}
                  onChange={(value) => setForm({ ...form, produto_id: value })}
                  disabled={loading || visualizando || editando}
                  required
                  placeholder="Buscar produto acabado..."
                />
              </Field>
              <Field label="Versão *" htmlFor="ficha-versao">
                <Input id="ficha-versao" value={form.versao} onChange={(event) => setForm({ ...form, versao: event.target.value })} disabled={loading || visualizando} required />
              </Field>
              <Field label="Rendimento *" htmlFor="ficha-rendimento">
                <DecimalInput id="ficha-rendimento" value={form.rendimento} onValueChange={(value) => setForm({ ...form, rendimento: value ?? 0 })} disabled={loading || visualizando} required />
              </Field>
              <Field label="Perda padrão (%)" htmlFor="ficha-perda">
                <DecimalInput id="ficha-perda" value={form.perda_padrao_percentual ?? ''} onValueChange={(value) => setForm({ ...form, perda_padrao_percentual: value })} disabled={loading || visualizando} />
              </Field>
              <Field label="Status" htmlFor="ficha-status">
                <Select id="ficha-status" value={form.status || 'RASCUNHO'} onChange={(event) => setForm({ ...form, status: event.target.value })} disabled={loading || visualizando}>
                  <option value="RASCUNHO">Rascunho</option>
                  <option value="APROVADA">Aprovada</option>
                  <option value="OBSOLETA">Obsoleta</option>
                </Select>
              </Field>
            </div>
          </section>

          <section className="nx-sngpc-form-section">
            <div className="nx-sngpc-form-section__header">
              <h2>Componentes</h2>
              {!visualizando ? (
                <Button type="button" variant="ghost" onClick={adicionarItem}>
                  <Plus size={15} />
                  Adicionar
                </Button>
              ) : null}
            </div>
            <div className="nx-table-wrap nx-sngpc-field--full">
              <table className="nx-table nx-sngpc-table">
                <thead>
                  <tr>
                    <th>Seq</th>
                    <th>Produto *</th>
                    <th>Quantidade *</th>
                    <th>Unidade</th>
                    <th>Perda %</th>
                    <th>Substituto</th>
                    {!visualizando ? <th className="nx-table-actions">Ações</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {(form.itens ?? []).length === 0 ? (
                    <tr>
                      <td colSpan={visualizando ? 6 : 7} className="nx-sngpc-empty-cell">Nenhum componente adicionado.</td>
                    </tr>
                  ) : (
                    (form.itens ?? []).map((item, index) => (
                      <tr key={index}>
                        <td>
                          <Input type="number" value={item.sequencia ?? ''} onChange={(event) => atualizarItem(index, { sequencia: Number(event.target.value) })} disabled={loading || visualizando} />
                        </td>
                        <td>
                          <div className="nx-selected-product-cell">
                            <strong>{produtoPorId(item.produto_id)?.codigo || '-'}</strong>
                            <span>{produtoPorId(item.produto_id)?.descricao || 'Produto não localizado'}</span>
                          </div>
                        </td>
                        <td>
                          <DecimalInput value={item.quantidade} onValueChange={(value) => atualizarItem(index, { quantidade: value ?? 0 })} disabled={loading || visualizando} required />
                        </td>
                        <td>
                          <Input value={item.unidade || ''} readOnly disabled={loading || visualizando} />
                        </td>
                        <td>
                          <DecimalInput value={item.perda_percentual ?? ''} onValueChange={(value) => atualizarItem(index, { perda_percentual: value ?? 0 })} disabled={loading || visualizando} />
                        </td>
                        <td>
                          <div className="nx-substitute-cell">
                            {item.substituto_id ? (
                              <div className="nx-selected-product-cell">
                                <strong>{produtoPorId(item.substituto_id)?.codigo || '-'}</strong>
                                <span>{produtoPorId(item.substituto_id)?.descricao || 'Produto não localizado'}</span>
                              </div>
                            ) : (
                              <span className="nx-substitute-cell__empty">Sem substituto</span>
                            )}
                            {!visualizando ? (
                              <div className="nx-substitute-cell__actions">
                                <Button type="button" variant="ghost" onClick={() => abrirModalSubstituto(index)} disabled={loading}>
                                  <Search size={15} />
                                  {item.substituto_id ? 'Alterar' : 'Selecionar'}
                                </Button>
                                {item.substituto_id ? (
                                  <Button type="button" variant="ghost" onClick={() => limparSubstituto(index)} disabled={loading}>
                                    Limpar
                                  </Button>
                                ) : null}
                              </div>
                            ) : null}
                          </div>
                        </td>
                        {!visualizando ? (
                          <td className="nx-table-actions">
                            <IconButton label="Remover" variant="danger" onClick={() => removerItem(index)}>
                              <Trash2 size={15} strokeWidth={1.9} aria-hidden />
                            </IconButton>
                          </td>
                        ) : null}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {custoCalculado > 0 ? (
              <p className="nx-form-hint" style={{ marginTop: '0.75rem' }}>
                <strong>Custo estimado:</strong> R$ {custoCalculado.toFixed(2).replace('.', ',')} por unidade
              </p>
            ) : null}
          </section>
        </fieldset>
      </Card>

      {visualizando ? (
        <FormStickyFooter>
          <div className="nx-form-footer-right" style={{ display: 'flex', alignItems: 'center', gap: '0.55rem', flexWrap: 'wrap', marginLeft: 'auto' }}>
            <Button type="button" variant="ghost" onClick={() => navigate('/producao/fichas-tecnicas')}>
              <ArrowLeft size={16} />
              Voltar
            </Button>
            {id ? (
              <Button type="button" onClick={() => navigate(`/producao/fichas-tecnicas/${id}/editar`)}>
                <Pencil size={16} />
                Editar
              </Button>
            ) : null}
          </div>
        </FormStickyFooter>
      ) : (
        <FormStickyFooter
          cancelLabel="Cancelar"
          onCancel={() => navigate('/producao/fichas-tecnicas')}
          saveLabel="Salvar"
          loading={salvando}
          saveIcon={<Save size={16} />}
        />
      )}

      <Dialog open={modalComponentesAberto} onOpenChange={setModalComponentesAberto}>
        <DialogContent className="nx-component-picker-dialog">
          <DialogHeader>
            <DialogTitle>Adicionar componentes</DialogTitle>
            <DialogDescription>Selecione um ou mais produtos para inserir na grade de componentes.</DialogDescription>
          </DialogHeader>

          <div className="nx-component-picker">
            <div className="nx-search-field nx-search-field--filter nx-component-picker__search">
              <Search size={15} strokeWidth={2} aria-hidden />
              <input
                type="search"
                value={buscaComponente}
                onChange={(event) => setBuscaComponente(event.target.value)}
                placeholder="Buscar por código ou descrição..."
                aria-label="Buscar componentes"
              />
            </div>

            <div className="nx-component-picker__meta">
              <span>{produtosComponentes.length} produtos encontrados</span>
              <span>{componentesSelecionados.length} selecionados</span>
            </div>

            <div className="nx-component-picker__list">
              {produtosComponentes.length === 0 ? (
                <div className="nx-component-picker__empty">Nenhum produto encontrado.</div>
              ) : (
                produtosComponentes.map((produto) => {
                  const uuid = getUuidCandidate(produto) || '';
                  const checked = componentesSelecionados.includes(uuid);
                  return (
                    <label key={produto.id} className="nx-component-picker__row">
                      <Checkbox checked={checked} onChange={() => toggleComponenteSelecionado(uuid)} />
                      <span className="nx-component-picker__product">
                        <strong>{produto.codigo || '-'}</strong>
                        <span>{produto.descricao}</span>
                      </span>
                      <span className="nx-component-picker__unit">{produto.unidade || '-'}</span>
                    </label>
                  );
                })
              )}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setModalComponentesAberto(false)}>
              Cancelar
            </Button>
            <Button type="button" onClick={confirmarComponentes} disabled={componentesSelecionados.length === 0}>
              <Plus size={16} />
              Adicionar selecionados
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={substitutoItemIndex !== null} onOpenChange={(open) => {
        if (!open) {
          setSubstitutoItemIndex(null);
          setBuscaSubstituto('');
        }
      }}>
        <DialogContent className="nx-component-picker-dialog">
          <DialogHeader>
            <DialogTitle>Selecionar substituto</DialogTitle>
            <DialogDescription>Escolha um produto para usar como substituto deste componente.</DialogDescription>
          </DialogHeader>

          <div className="nx-component-picker">
            <div className="nx-search-field nx-search-field--filter nx-component-picker__search">
              <Search size={15} strokeWidth={2} aria-hidden />
              <input
                type="search"
                value={buscaSubstituto}
                onChange={(event) => setBuscaSubstituto(event.target.value)}
                placeholder="Buscar substituto por código ou descrição..."
                aria-label="Buscar substituto"
              />
            </div>

            <div className="nx-component-picker__meta">
              <span>{produtosSubstitutos.length} produtos encontrados</span>
              <span>Seleção única</span>
            </div>

            <div className="nx-component-picker__list">
              {produtosSubstitutos.length === 0 ? (
                <div className="nx-component-picker__empty">Nenhum substituto encontrado.</div>
              ) : (
                produtosSubstitutos.map((produto) => {
                  const uuid = getUuidCandidate(produto) || '';
                  return (
                    <button
                      type="button"
                      key={produto.id}
                      className="nx-component-picker__row nx-component-picker__row--button"
                      onClick={() => selecionarSubstituto(uuid)}
                    >
                      <span className="nx-component-picker__product">
                        <strong>{produto.codigo || '-'}</strong>
                        <span>{produto.descricao}</span>
                      </span>
                      <span className="nx-component-picker__unit">{produto.unidade || '-'}</span>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => {
              setSubstitutoItemIndex(null);
              setBuscaSubstituto('');
            }}>
              Cancelar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </form>
  );
}
